import {
  Paper,
  Typography,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
  Box,
  Button,
  TextField,
  styled,
  FormControl,
  MenuItem,
  Checkbox,
} from '@mui/material';
import Page from '../components/Page';
import { taxTypes } from '../utils/hardcoded';
import * as yup from 'yup';
import { Formik } from 'formik';
import { store } from '../store';
import {
  cleanMailingState,
  confirmMailing,
  mailingError,
  mailingMessage,
  mailingStatus,
} from '../features/mailing/mailingSlice';
import { useSelector } from 'react-redux';
import CustomAlert from '../components/CustomAlert';
import UncheckedIcon from '../assets/icons/uncheckbox-icon';
import CheckboxIcon from '../assets/icons/checkbox-icon';
import TermsModal from '../components/TermsModal';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { getLabelClaveComprobante } from '../utils';

const FormWrapper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '1.5rem 2rem 2rem 2rem',
  justifyContent: 'space-between',
  border: '2px solid',
  borderColor: theme.palette.primary.main,
  marginLeft: '2rem',
  [theme.breakpoints.down('lg')]: {
    marginLeft: 0,
    marginTop: '2rem',
    marginBottom: '1rem',
  },
}));

const MainWrapper = styled(Paper)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  display: 'flex',
  flexDirection: 'column',
  padding: '2rem',
}));

export default function Boletas() {
  const theme = useTheme();
  const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const [openModal, setOpenModal] = useState(false);

  const error = useSelector(mailingError);
  const status = useSelector(mailingStatus);
  const message = useSelector(mailingMessage);

  const voucherValidationSchema = yup.object().shape({
    email: yup
      .string()
      .email('Ingrese un email válido')
      .required('El campo email es obligatorio'),
    reemail: yup
      .string()
      .oneOf([yup.ref('email'), null], 'Los emails tienen que coincidir')
      .required('El campo repetir email es obligatorio'),
    tipo: yup
      .string()
      .test(
        'is-required',
        'Imponible es requerido',
        value => value !== undefined
      ),
    id: yup
      .string()
      .when('tipo', {
        is: 'INM',
        then: yup.string().required('El campo partida es obligatorio'),
      })
      .when('tipo', {
        is: 'CEM',
        then: yup.string().required('El campo número de nicho es obligatorio'),
      })
      .when('tipo', {
        is: 'ROD',
        then: yup.string().required('El campo dominio es obligatorio'),
      })
      .when('tipo', {
        is: 'COM',
        then: yup.string().required('El campo número de comercio es obligatorio'),
      })
      .when('tipo', {
        is: 'CON',
        then: yup
          .string()
          .length(11, 'El Cuit debe ser de 11 caracteres')
          .required('El campo CUIT/CUIL es obligatorio'),
      }),
    terms: yup
      .boolean()
      .oneOf([true], 'Aceptar términos & condiciones es requerido'),
  });

  return (
    <Page>
      <Container>
        <Grid
          container
          padding={'1.875rem'}
          justifyContent="center"
          direction={desktop ? 'row' : 'column'}
        >
          <Grid item sm={10} sx={{ alignSelf: 'center' }}>
            <Box mb={3}>
              <MainWrapper>
                <Grid container direction="row">
                  <Grid item md={12} lg={7}>
                    <Typography variant="subtitle1" mb="1rem">
                      Boleta por mail
                    </Typography>
                    <Typography variant="body1" mb=".5rem">
                      Permite que te suscribas al servicio de recepción por mail
                      de las boletas para el pago de las tasas de Inmuebles,
                      Rodados, Comercios y Contribuyentes.
                    </Typography>
                    <Typography variant="body1" mb="1rem">
                      Admite más de una suscripción por imponible para que, por
                      ejemplo, puedan recibirla todos los titulares; modificar
                      los mails suscriptos e incluso eliminar las cuentas donde
                      ya no desees recibirla.
                    </Typography>

                    <Typography variant="body1" mb=".5rem">
                      <strong>Requisitos</strong>
                    </Typography>
                    <Typography variant="body1">
                      1) Dirección de correo electrónico (mail)
                    </Typography>
                    <Typography variant="body1">
                      2) Número de CUIT, CUIL o CDI
                    </Typography>
                    <Typography variant="body1">
                      3) Identificación del Objeto:
                    </Typography>
                    <Box ml=".5rem">
                      <Typography variant="body1">
                        - Para tasas a los Inmuebles: Número de partida.
                      </Typography>
                      <Typography variant="body1">
                        - Para tasas a los Contribuyentes: Número de CUIT, o CUIL.
                      </Typography>
                      <Typography variant="body1">
                        - Para tasas a los Comercios: Número de Comercio.
                      </Typography>
                      <Typography variant="body1">
                        - Para tasas a los Rodados: Número de dominio (patente).
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={12} lg={5}>
                    <Box>
                      <Formik
                        initialValues={{
                          email: '',
                          reemail: '',
                          tipo: '',
                          id: '',
                          terms: false,
                        }}
                        validationSchema={voucherValidationSchema}
                        onSubmit={values =>
                          store.dispatch(confirmMailing(values))
                        }
                      >
                        {({
                          handleChange,
                          handleSubmit,
                          setFieldValue,
                          values,
                          errors,
                          touched,
                        }) => (
                          <FormWrapper>
                            <Typography
                              variant="h6"
                              component="h2"
                              mb="1rem"
                              sx={{ alignSelf: 'center' }}
                            >
                              Completá tus datos
                            </Typography>

                            <FormControl fullWidth>
                              <TextField
                                variant="outlined"
                                id="email"
                                label={'Email'}
                                value={values.email}
                                onChange={handleChange('email')}
                                sx={{ marginBottom: '1rem' }}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                              />

                              <TextField
                                variant="outlined"
                                id="reemail"
                                label={'Repetir email'}
                                value={values.reemail}
                                onChange={handleChange('reemail')}
                                sx={{ marginBottom: '1rem' }}
                                error={touched.reemail && Boolean(errors.reemail)}
                                helperText={touched.reemail && errors.reemail}
                              />

                              <TextField
                                select
                                id="taxable"
                                label="Imponible"
                                value={values.tipo}
                                onChange={handleChange('tipo')}
                                sx={{ marginBottom: '1rem' }}
                                error={touched.tipo && Boolean(errors.tipo)}
                                helperText={touched.tipo && errors.tipo}
                              >
                                {taxTypes.map((tax, i) => (
                                  <MenuItem key={i} value={tax.value}>
                                    {tax.name}
                                  </MenuItem>
                                ))}
                              </TextField>

                              {values.tipo !== '' ? (
                                <TextField
                                  variant="outlined"
                                  id="taxabletype"
                                  value={values.id}
                                  onChange={e => {
                                    setFieldValue(
                                      'id',
                                      e.target.value.toLocaleUpperCase()
                                    );
                                  }}
                                  sx={{ marginBottom: '1rem' }}
                                  error={touched.id && Boolean(errors.id)}
                                  helperText={touched.id && errors.id}
                                  label={ getLabelClaveComprobante(values.tipo) }
                                />
                              ) : null}
                            </FormControl>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom:
                                  errors.terms && touched.terms
                                    ? '0rem'
                                    : '1rem',
                              }}
                            >
                              <Checkbox
                                onClick={() =>
                                  setFieldValue('terms', !values.terms)
                                }
                                icon={<UncheckedIcon />}
                                checkedIcon={<CheckboxIcon />}
                                {...label}
                              />

                              <Typography>
                                Acepto los{' '}
                                <span
                                  onClick={() => setOpenModal(!openModal)}
                                  style={{
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                  }}
                                >
                                  términos y condiciones para el uso del
                                  servicio.
                                </span>
                              </Typography>
                            </Box>
                            {errors.terms && touched.terms && (
                              <Typography
                                sx={{
                                  fontSize: '.75rem',
                                  marginX: '1rem',
                                  marginY: '.5rem',
                                  color: '#D64548',
                                }}
                              >
                                {errors.terms}
                              </Typography>
                            )}

                            <CustomAlert
                              status={status}
                              message={message}
                              errorMessage={error}
                              style={{ marginBottom: '1rem' }}
                            />

                            <Box sx={{ display: 'flex', width: '100%' }}>
                              <LoadingButton
                                loading={
                                  status === 'idle' || status === 'error'
                                    ? false
                                    : true
                                }
                                variant="contained"
                                onClick={() => handleSubmit()}
                                sx={{
                                  color: 'white',
                                  width: '100%',
                                  marginRight: '1rem',
                                }}
                              >
                                Suscribir
                              </LoadingButton>
                              <Button
                                variant="outlined"
                                sx={{ width: '100%' }}
                                onClick={() => {
                                  setFieldValue('email', '');
                                  setFieldValue('tipo', '');
                                  setFieldValue('id', '');
                                  setFieldValue('reemail', '');
                                  store.dispatch(cleanMailingState());
                                }}
                              >
                                Cancelar
                              </Button>
                            </Box>
                          </FormWrapper>
                        )}
                      </Formik>
                      <TermsModal
                        openModal={openModal}
                        setOpenModal={setOpenModal}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </MainWrapper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
